import * as React from 'react';
import { flow } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IStore } from '../../../../redux/IStore';
import NavigationBar from '../../components/NavigationBar';
import {Form, FormGroup, Col} from 'react-bootstrap';
import {actions as quoteActions } from '../../../../services/quote/reducer';
import {
  formValueSelector,
  InjectedFormProps,
  reduxForm, submit,
} from 'redux-form';
import {IActivities, IChannel, IIAS, IProduct} from '../../../../data/api/models';
import {IAuthState} from '../../../../services/auth/models';
import {II18nMessages} from '../../../../services/branding/models';
import {IQuoteActions, IQuoteState} from '../../../../services/quote/models';
import {hasSameValue} from '../../../../util/validators';
import DeclarationQuestions from '../../components/DeclarationQuestions';
import DeclinesAndReferrals from '../../components/DeclinesAndReferrals';
import {getReferralsAndDeclines} from '../../../../util/helpers';
import {Email} from '../../../../components/Email';
import {CheckboxGroup} from '../../../../components/CheckboxGroup';

interface IIASFormProps extends InjectedFormProps {
  dispatch: any;
  auth: IAuthState;
  startDate: string;
  product: IProduct;
  renewal: boolean;
  channel: IChannel;
  i18n: II18nMessages;
  quote: IQuoteState;
  renderDefinition(name: string): any;
  actions: {
    quote: IQuoteActions;
    submit: any;
  };
  revenue: number;

  ias: IIAS;
  activities: IActivities;
  triggerActivityValidation: boolean;
}

const MAX_IAS_PAYMENTS_ADJUSTED = 150000;

class IASForm extends React.Component<IIASFormProps, any> {
  public state = {
    declines: [],
    referrals: [],
    errors: [],
    iasNext: {},
    showPrimaryQuestions: false,
  };

  public componentDidMount() {
    const { ias, revenue } = this.props;
    this.setState({ showPrimaryQuestions: (ias && ias.primary) || revenue > 50000 });
  }

  public componentWillReceiveProps(nextProps) {
    const { ias, dispatch, change } = this.props;
    if (nextProps.hasOwnProperty('ias')) {
      this.resetAnswers(nextProps.ias);
    }

    const newIASPayments = nextProps.ias.payments;
    if (ias && ias.payments !== newIASPayments) {
      if (newIASPayments === '' || newIASPayments === null || nextProps.ias.declaration[9] !== 'true') {
        dispatch(change('ias.payments', ''));
        dispatch(change('ias.paymentsAdjusted', 0));
      } else {
        const newIASPaymentsAdjusted = Math.max((newIASPayments / 12) * 2, 115000);
        dispatch(change('ias.payments', newIASPayments));
        dispatch(change('ias.paymentsAdjusted', newIASPaymentsAdjusted));
      }
    }

    // --------------------------------------------------------------------------------------
    // ias.activity._changed is used to make the CheckboxGroup validation work always
    // --------------------------------------------------------------------------------------
    if (ias && (ias.activity.intermediary !== nextProps.ias.activity.intermediary
      || ias.activity.broker !== nextProps.ias.activity.broker
      || ias.activity.agent !== nextProps.ias.activity.agent)) {
      const applyDiscount = nextProps.ias.activity.intermediary
        && ! nextProps.ias.activity.broker
        && ! nextProps.ias.activity.agent;
      dispatch(change('ias.activity.discount', applyDiscount ? 'true' : 'false'));
      dispatch(change('ias.activity._changed', ! ias.activity._changed));
    }

    const { quote } = this.props;
    const referralsAndDeclines = getReferralsAndDeclines(quote);
    if (this.state.declines !== referralsAndDeclines.declines) {
      this.setState({ declines: referralsAndDeclines.declines });
    }
    if (this.state.errors !== referralsAndDeclines.errors) {
      this.setState({ errors: referralsAndDeclines.errors });
    }
  }

  private resetAnswers(nextIAS: IIAS) {
    const { dispatch, change, ias } = this.props;

    if (!ias) {
      return;
    }

    if (this.state.showPrimaryQuestions) {
      if (! hasSameValue(nextIAS.declaration, ias.declaration, '2') && nextIAS.declaration[2] !== 'true') {
        if (nextIAS.declaration[3] !== '') {
          dispatch(change('ias.declaration[3]', ''));
        }
      }

      if (! hasSameValue(nextIAS.declaration, ias.declaration, '6') && nextIAS.declaration[6] !== 'true') {
        if (nextIAS.declaration[7] !== '0') {
          dispatch(change('ias.declaration[7]', '0'));
        }
        if (nextIAS.declaration[8] !== '') {
          dispatch(change('ias.declaration[8]', ''));
        }
      }
      if (! hasSameValue(nextIAS.declaration, ias.declaration, '9') && nextIAS.declaration[9] !== 'true') {
        if (nextIAS.payments !== '0') {
          dispatch(change('ias.payments', '0'));
        }
      }
    } else {
      if (! hasSameValue(nextIAS.declaration, ias.declaration, '10') && nextIAS.declaration[10] !== 'true') {
        dispatch(change('ias.declaration[5]', ''));
        dispatch(change('ias.declaration[6]', ''));
        dispatch(change('ias.declaration[7]', '0'));
        dispatch(change('ias.declaration[8]', ''));
        dispatch(change('ias.declaration[9]', ''));
        dispatch(change('ias.payments', '0'));
      }

      if (! hasSameValue(nextIAS.declaration, ias.declaration, '6') && nextIAS.declaration[6] !== 'true') {
        if (nextIAS.declaration[7] !== '0') {
          dispatch(change('ias.declaration[7]', '0'));
        }
        if (nextIAS.declaration[8] !== '') {
          dispatch(change('ias.declaration[8]', ''));
        }
      }

      if (! hasSameValue(nextIAS.declaration, ias.declaration, '9') && nextIAS.declaration[9] !== 'true') {
        if (nextIAS.payments !== '0') {
          dispatch(change('ias.payments', '0'));
        }
      }
    }
  }

  private getQuestions() {
    const { ias } = this.props;
    const questions = [];
    const intermediaryOnly = ias && ias.activity.intermediary && !ias.activity.broker && !ias.activity.agent;

    const allQuestions = {
      1: {
        field: 'ias.declaration[1]',
        question: 'Réalisez-vous + de 70 % de votre chiffre d’affaires '
        + 'en assurances de personnes (santé prévoyance) ?',
      },
      2: {
        field: 'ias.declaration[2]',
        question: 'Proposez-vous tout type d\'assurances y compris IARD ?',
      },
      3: {
        field: 'ias.declaration[3]',
        question: 'Réalisez-vous + de 20 % de votre chiffre d\'affaires en assurance Construction'
        + ' (Dommage ouvrage et/ou RC Décennale) et/ou en Evénementiel-annulation ?',
      },
      5: {
        field: 'ias.declaration[5]',
        question: 'Avez-vous signé un mandat de délégation de souscription et/ou '
          + 'de gestion de sinistres avec un assureur ?',
      },
      6: {
        field: 'ias.declaration[6]',
        question: 'Avez-vous des mandataires personnes physiques que vous souhaitez assurer dans votre contrat ?',
      },
      7: {
        field: 'ias.declaration[7]',
        question: 'Combien ?',
        fieldType: 'number',
      },
      8: {
        field: 'ias.declaration[8]',
        question: 'Compte tenu du nombre d\'assurés, souhaitez vous souscrire un relèvement du '
        + 'montant de la garantie RC Professionnelle par période d\'assurance ?',
      },
      9: {
        field: 'ias.declaration[9]',
        question: 'Souhaitez vous souscrire une garantie financière ?',
      },
      10: {
        field: 'ias.declaration[10]',
        question: 'Commercialisez-vous exclusivement des assurances emprunteurs ?',
      },
      payments: {
        field: 'ias.payments',
        question: 'Montant des fonds encaissés effectués au cours des 12 derniers mois (primes, sinistres) ?',
        fieldType: 'currency',
      },
    };

    if (this.state.showPrimaryQuestions) {
      questions.push(...[
        allQuestions[1],
        allQuestions[2],
        {type: 'divider'},
      ]);

      if (ias && ias.declaration[2] === 'true') {
        questions.push(allQuestions[3]);
      }

      questions.push(allQuestions[5]);

      if (! intermediaryOnly) {
        questions.push(allQuestions[6]);
      }

      if (ias && ias.declaration[6] === 'true') {
        questions.push(allQuestions[7]);
        questions.push(allQuestions[8]);
      }

      questions.push({type: 'divider'});
      questions.push(allQuestions[9]);

      if (ias && ias.declaration[9] === 'true') {
        questions.push(allQuestions.payments);
      }
    } else {
      questions.push(allQuestions[10]);
      questions.push(allQuestions[5]);

      if (! intermediaryOnly) {
        questions.push(allQuestions[6]);
      }
      if (ias && ias.declaration[6] === 'true') {
        questions.push(allQuestions[7]);
        questions.push(allQuestions[8]);
      }
      questions.push(allQuestions[9]);
      if (ias && ias.declaration[9] === 'true') {
        questions.push(allQuestions.payments);
      }
    }

    return questions;
  }

  private declinesOrReferrals() {
    const { declines, referrals, errors } = this.state;

    if (! declines.length && ! referrals.length && !errors.length) {
      return null;
    }

    return(
      <div>
        <DeclinesAndReferrals
          declines={declines}
          referrals={referrals}
          errors={errors}
          quoteReference={this.props.quote.application.quoteReference}
          renewal={this.props.renewal}
        />
        {(! declines.length && ! referrals.length && errors.length) && (
          <NavigationBar
            hasBackButton={true}
            backTo={'/'}
            hasNextButton={false}
          />
        )}
      </div>
    );
  }

  private validateIASPayments = () => {
    const { ias } = this.props;
    if (!ias) {
      return true;
    }
    return ias.paymentsAdjusted <= MAX_IAS_PAYMENTS_ADJUSTED;
  }

  private extraValidation = () => {
    return this.validateIASPayments();
  }

  public render() {
    const { handleSubmit, ias, activities, renderDefinition } = this.props;
    const questions = this.getQuestions();

    const declinesOrReferrals = this.declinesOrReferrals();
    if (declinesOrReferrals) {
      return declinesOrReferrals;
    }

    const isValid = this.extraValidation();

    return (
      <Form horizontal={true}
            onSubmit={handleSubmit}
            autoComplete="off">
        <div className="steps-container">
          <h3>
            {ias && ias.primary && parseFloat(activities.intermediary.percent) >= 50 ?
              'Votre activité principale' : 'Activité Secondaire'} : Intermédiaire en Assurance (IAS)
          </h3>

          <FormGroup controlId={'ias.activity-xs'}>
            <Col sm={2}>
              {renderDefinition('ias')}
              Votre Statut
            </Col>
            <Col sm={8}>
              <div className={'radio-group'}>
                <div className="">
                  <CheckboxGroup
                    name={'ias.activity-list'}
                    values={{ias}}
                    fields={[
                      {
                        name: 'ias.activity.broker',
                        label: 'Courtier d\'assurance',
                      },
                      {
                        name: 'ias.activity.agent',
                        label: 'Mandataire d\'assurance',
                      },
                      {
                        name: 'ias.activity.intermediary',
                        label: 'Mandataire d\'Intermédiaire d\'assurance',
                      },
                    ]}
                    {...this.props}
                  />
                </div>
              </div>
            </Col>
          </FormGroup>
          <hr/>

          <strong>Informations complémentaires sur votre activité</strong>

          <DeclarationQuestions renderDefinition={renderDefinition} questions={questions} />

          {! this.validateIASPayments() && (
            <div className="alert alert-danger">
              <p>
                Le montant des fonds encaissés ne permet pas la réalisation d'un devis en ligne. Merci de nous contacter
                à l'adresse suivante : <Email email={'rcpro.france@msamlin.com'}/> et nous reviendrons vers vous dans
                les meilleurs délais.
              </p>
            </div>
          )}

          <NavigationBar
            hasBackButton={true}
            backTo={'/'}
            nextButtonDisabled={!isValid}
            {...this.props}
          />
        </div>
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');
    return component;
  },
  connect(
    (state: IStore) => {
      const startDate = selector(state, 'startDate');
      const quotationTypes = selector(state, 'quotationTypes');
      const ias = selector(state, 'ias');
      const activities = selector(state, 'activities');
      const revenue = selector(state, 'revenue');

      return {
        startDate,
        quotationTypes,
        auth: state.auth,
        ias,
        activities,
        revenue,
      };
    },
    (dispatch) => ({
      actions: {
        submit: bindActionCreators(submit, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(IASForm);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/IAS/index.tsx